// ─── targetAgent.js ───────────────────────────────────────────────────────────
// Real agent for Target Redsky API (via Anthropic mock).
// Outputs { item, brand, price, rating, inStock, store } + Target extras.
// ─────────────────────────────────────────────────────────────────────────────

// Calls our local proxy server (server.js) which forwards to Anthropic.
// The API key stays on the server — never exposed to the browser.
const ANTHROPIC_API = '/api/anthropic';
const MODEL         = 'claude-haiku-4-5-20251001';

function buildPrompt(query) {
  return `Return ONLY a raw JSON object. No markdown, no explanation, no extra text.

Simulate a Target Redsky API search response for: "${query}"

Return exactly 3 products using this schema:
{
  "items": [
    {
      "title": "Full product title",
      "brand": "Brand name",
      "price": 59.99,
      "original_price": 79.99,
      "rating": 4.4,
      "review_count": 1820,
      "tcin": "12345678",
      "dpci": "058-06-0023",
      "seller_type": "Target",
      "seller_name": "Target",
      "has_circle_deal": false,
      "circle_label": null,
      "circle_percent": 0,
      "redcard_price": 56.99,
      "is_drive_up": true,
      "is_same_day": false,
      "is_order_pickup": false,
      "is_target_plus": false,
      "shipping_free": true,
      "shipping_cost": 0,
      "store_count": 1450,
      "availability": "In Stock"
    }
  ]
}

Rules:
- REAL Target product names, brands, and realistic prices for "${query}"
- Brands: Apple, Sony, Beats, Ninja, Instant, Keurig, TCL, Threshold, Room Essentials, Good & Gather
- tcin: realistic 8-digit Target item number
- dpci: format "DDD-CC-IIII" e.g. "058-06-0023"
- has_circle_deal: true for 1 item, circle_label like "15% off with Circle", circle_percent: 15
- redcard_price: always price * 0.95 (5% RedCard discount)
- is_drive_up: true for 2 items
- is_same_day: true for 1 item (Shipt eligible)
- is_target_plus: true for 1 item (third-party seller on target.com)
- shipping_free: true if price >= 35
- store_count: 1000–1900
- original_price higher than price for 2 items, same for 1
- Price at low / mid / premium tiers for "${query}"`;
}

async function callAPI(prompt) {
  const response = await fetch(ANTHROPIC_API, {
    method:  'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      model:      MODEL,
      max_tokens: 2000,
      messages:   [{ role: 'user', content: prompt }],
    }),
  });
  if (!response.ok) throw new Error(`Target agent API error: ${response.status}`);
  const data    = await response.json();
  const rawText = data.content?.find(b => b.type === 'text')?.text || '';
  const first   = rawText.indexOf('{');
  const last    = rawText.lastIndexOf('}');
  if (first === -1) throw new Error('Target agent: no JSON found');
  return JSON.parse(rawText.slice(first, last + 1));
}

function normalizeItem(raw, store) {
  const price         = parseFloat(raw.price)          || 0;
  const originalPrice = parseFloat(raw.original_price) || null;
  const discountPct   = originalPrice && originalPrice > price
    ? Math.round((1 - price / originalPrice) * 100) : null;

  return {
    item:           raw.title           || 'Unknown Product',
    brand:          raw.brand           || 'Unknown',
    price,
    rating:         parseFloat(raw.rating) || 4.0,
    inStock:        raw.availability    !== 'Out of Stock',
    store:          store.id,

    // Target-specific extras
    tcin:           raw.tcin            || null,
    dpci:           raw.dpci            || null,
    url:            raw.url             || `https://www.target.com/s?searchTerm=${encodeURIComponent(raw.title||'')}`,
    original_price: originalPrice,
    discount_pct:   discountPct,
    review_count:   parseInt(raw.review_count) || 0,
    seller_type:    raw.is_target_plus  ? 'Target+' : 'Target',
    seller_name:    raw.seller_name     || 'Target',
    is_target_plus: raw.is_target_plus  === true,
    has_circle_deal:raw.has_circle_deal === true,
    circle_label:   raw.circle_label    || null,
    circle_percent: parseInt(raw.circle_percent) || 0,
    redcard_price:  parseFloat(raw.redcard_price) || +(price * 0.95).toFixed(2),
    redcard_savings:+(price * 0.05).toFixed(2),
    is_drive_up:    raw.is_drive_up     === true,
    is_same_day:    raw.is_same_day     === true,
    is_order_pickup:raw.is_order_pickup === true,
    shipping_free:  raw.shipping_free   === true,
    shipping_cost:  parseFloat(raw.shipping_cost) || 0,
    store_count:    parseInt(raw.store_count) || 0,
    total_cost:     +(price + (raw.shipping_free ? 0 : (parseFloat(raw.shipping_cost) || 0))).toFixed(2),
    marketplace:    'target',
  };
}

export async function runTargetAgent(store, query) {
  const prompt = buildPrompt(query);
  const parsed = await callAPI(prompt);
  const items  = Array.isArray(parsed.items) ? parsed.items : [];
  return items.map(item => normalizeItem(item, store));
}

export default runTargetAgent;
